import { takeLatest, put, select, all, delay } from 'redux-saga/effects';
import { dashboardsSelector } from '@store/tempDashboard/selectors';
import {
  appLiveModeOnTrendsSelector,
  realTimeOnTrendsFeatureSelector,
} from '@store/common/selectors';
import {
  applyFilter,
  toggleLayout,
  updateGranularity,
  updateInterval,
} from '@store/common/actions';
import { resetDrillDown } from '@store/filterContainer/actions';
import { pushDrilldownFilter, pushMultiDrilldownFilter } from '@store/dimensionContainer/actions';

import { selectedMetricChart, setMetrics } from '@store/metricsChartContainer/actions';
import {
  DATE_SHORTCUTS,
  NOC_DRILLDOWN,
  DASHBOARD_TYPE,
  LAYOUT_MAP,
  isAppModule,
} from '@utils/constants';
import { METRICS_MAPPING, calcShortcutInterval } from '@utils';
import { changeMode } from '@store/trends/actions';
import * as types from './types';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import { updateDrilldownState } from '@store/trends/sharing/sagas';
import { applyCompareFiltersOnTrends, setDimensions } from '@store/trends/base/actions';

// Custom Dashboard Drilldown
function* initVdashDrilldown(dashboard) {
  try {
    const { filter, metrics, interval, dimensions, comparedFilters, drillDown } = dashboard;
    let intervalParams = null;
    let metricsToSet = metrics;

    const mappedMetrics = [];
    metrics.forEach((metric) => {
      if (metric in METRICS_MAPPING) {
        mappedMetrics.push(METRICS_MAPPING[metric]);
      }
    });
    metricsToSet = combineWithMetricDeDuplication(metricsToSet, mappedMetrics);

    let granularity = '';

    if (interval) {
      intervalParams = [interval.from, interval.to, interval.shortcut, 'trends'];
      granularity = interval.granularity || '';
    } else {
      const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last6Hours);
      intervalParams = [from.format(), to.format(), DATE_SHORTCUTS.last6Hours, 'trends'];
    }

    const tasks: any = [
      // set to current interval
      put(updateInterval(...intervalParams)),
    ];

    if (granularity) {
      tasks.push(put(updateGranularity(granularity)));
    }

    // reset and appply filter
    tasks.push(
      put(resetDrillDown()),
      put(pushMultiDrilldownFilter(drillDown)),
      put(applyFilter(filter)),
    );

    // update selected metric and enable diagnostics
    if (metrics.length === 1) {
      tasks.push(put(selectedMetricChart(metrics[0])));
    }
    tasks.push(put(setMetrics(metricsToSet)));

    // update selected dimension
    if (dimensions?.length) {
      tasks.push(put(setDimensions(dimensions)));
    }

    // update compared filters
    if (comparedFilters?.length) {
      tasks.push(put(applyCompareFiltersOnTrends(comparedFilters)));
    }

    yield all(tasks);
    yield delay(300);
    yield put({ type: types.END_FETCHING });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// noc drilldown action
function* initNocDrilldown(dashboard, type = null) {
  try {
    const { filter, metric, leftPanelMetrics = [], interval } = dashboard;
    let intervalParams = null;
    let metricsToSet = [metric];
    if (leftPanelMetrics && leftPanelMetrics.length) {
      metricsToSet = [...leftPanelMetrics];
    }

    if (metric in METRICS_MAPPING) {
      metricsToSet = combineWithMetricDeDuplication(metricsToSet, [METRICS_MAPPING[metric]]);
    }

    let granularity = '';

    if (interval) {
      intervalParams = [interval.from, interval.to, interval.shortcut, 'trends'];

      granularity = interval.granularity || '';
    } else {
      const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last6Hours);
      intervalParams = [from.format(), to.format(), DATE_SHORTCUTS.last6Hours, 'trends'];
    }

    const rtOnTrendsEnabled = yield select(realTimeOnTrendsFeatureSelector);
    const isAppLiveModeOnTrendsEnabled = yield select(appLiveModeOnTrendsSelector);

    const isRtDrilldown =
      type && [DASHBOARD_TYPE.NOC_DRILLDOWN, DASHBOARD_TYPE.APP_NOC_DRILLDOWN].includes(type);
    if ((isAppModule ? isAppLiveModeOnTrendsEnabled : rtOnTrendsEnabled) && isRtDrilldown) {
      const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.last15Mins);
      intervalParams = [from.format(), to.format(), DATE_SHORTCUTS.last15Mins, 'trends'];
    }

    const tasks: any = [
      put(changeMode(NOC_DRILLDOWN)),
      put(toggleLayout(LAYOUT_MAP.horizontal)),
      // set to current interval
      put(updateInterval(...intervalParams)),
    ];

    if (granularity) {
      tasks.push(put(updateGranularity(granularity)));
    }

    // reset and appply filter
    tasks.push(
      put(resetDrillDown()),
      put(pushDrilldownFilter(filter.name, filter.rule)),
      put(applyFilter(filter)),
    );

    // update selected metric and enable diagnostics
    tasks.push(
      put(selectedMetricChart(metric)),
      put(setMetrics(metricsToSet)),
      // put(toggleDiagChart(true)),
    );

    yield all(tasks);
    yield delay(300);
    yield put({ type: types.END_FETCHING });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// drilldown action
function* initDrilldown({ state }) {
  try {
    yield updateDrilldownState(state);
    yield delay(300);
    yield put({ type: types.END_FETCHING });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// could be generalized to dispatch based on dashboard type
function* initTempDashboard({ tempDashboardId }) {
  const dashboards = yield select(dashboardsSelector);
  const tempDashboard = dashboards[tempDashboardId];

  if (!tempDashboard || !tempDashboard.dashboard) {
    return;
  }

  yield put({ type: types.START_FETCHING });
  const { dashboard, type } = tempDashboard;
  // usecaseKey overrides for app workflows
  // we can fix this after consolding all trends drilldowns to have on flow
  if (type === DASHBOARD_TYPE.CUSTOM_DASHBOARD_DRILLDOWN) {
    yield initVdashDrilldown(dashboard);
  } else if ([DASHBOARD_TYPE.TRENDS_DRILLDOWN].includes(type) || 'usecaseKey' in dashboard) {
    yield initDrilldown({ state: dashboard });
  } else {
    yield initNocDrilldown(dashboard, type);
  }
}

function* watchInitTempDashboard() {
  yield takeLatest([types.INIT_TEMP_DASHBOARD], initTempDashboard);
}

function* watchInitDrilldown() {
  yield takeLatest([types.INIT_DRILLDOWN], initDrilldown);
}

export default [watchInitTempDashboard, watchInitDrilldown];
